import { userApi as api } from './userApi';
export const addTagTypes = [
  'UserDeviceTokens',
  'Users',
  'Authentication',
  'isEmailExist'
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      addUserDeviceTokens: build.mutation<
        AddUserDeviceTokensApiResponse,
        AddUserDeviceTokensApiArg
      >({
        query: queryArg => ({
          url: `/userDeviceTokens`,
          method: 'POST',
          body: queryArg.createUserDeviceTokens
        }),
        invalidatesTags: ['UserDeviceTokens']
      }),
      getUsers: build.query<GetUsersApiResponse, GetUsersApiArg>({
        query: queryArg => ({
          url: `/users`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['Users']
      }),
      addUsers: build.mutation<AddUsersApiResponse, AddUsersApiArg>({
        query: queryArg => ({
          url: `/users`,
          method: 'POST',
          body: queryArg.createUsers
        }),
        invalidatesTags: ['Users']
      }),
      getUsersById: build.query<GetUsersByIdApiResponse, GetUsersByIdApiArg>({
        query: queryArg => ({ url: `/users/${queryArg.id}` }),
        providesTags: ['Users']
      }),
      updateUsers: build.mutation<UpdateUsersApiResponse, UpdateUsersApiArg>({
        query: queryArg => ({
          url: `/users/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateUsers
        }),
        invalidatesTags: ['Users']
      }),
      deleteUsers: build.mutation<DeleteUsersApiResponse, DeleteUsersApiArg>({
        query: queryArg => ({
          url: `/users/${queryArg.id}`,
          method: 'DELETE',
          params: { delete: queryArg['delete'] }
        }),
        invalidatesTags: ['Users']
      }),
      blockUnblockUser: build.mutation<
        BlockUnblockUserApiResponse,
        BlockUnblockUserApiArg
      >({
        query: queryArg => ({
          url: `/users/${queryArg.id}/block-status`,
          method: 'PUT',
          body: queryArg.blockUnblockUser
        }),
        invalidatesTags: ['Users']
      }),
      forgotPassword: build.mutation<
        ForgotPasswordApiResponse,
        ForgotPasswordApiArg
      >({
        query: queryArg => ({
          url: `/forgotPassword`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Authentication']
      }),
      verifySignupCode: build.mutation<
        VerifySignupCodeApiResponse,
        VerifySignupCodeApiArg
      >({
        query: queryArg => ({
          url: `/verifySignupCode`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Authentication']
      }),
      resetPassword: build.mutation<
        ResetPasswordApiResponse,
        ResetPasswordApiArg
      >({
        query: queryArg => ({
          url: `/resetPassword`,
          method: 'POST',
          body: queryArg.resetPasswordSchema
        }),
        invalidatesTags: ['Users']
      }),
      postForgotPassword: build.mutation<
        PostForgotPasswordApiResponse,
        PostForgotPasswordApiArg
      >({
        query: queryArg => ({
          url: `/postForgotPassword`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Users']
      }),
      isEmailExist: build.mutation<IsEmailExistApiResponse, IsEmailExistApiArg>(
        {
          query: queryArg => ({
            url: `/is-email-exist`,
            method: 'POST',
            body: queryArg.isEmailExist
          }),
          invalidatesTags: ['isEmailExist']
        }
      ),
      me: build.query<MeApiResponse, MeApiArg>({
        query: () => ({ url: `/me` }),
        providesTags: ['Users']
      }),
      searchUsers: build.query<SearchUsersApiResponse, SearchUsersApiArg>({
        query: queryArg => ({
          url: `/search`,
          params: { q: queryArg.q, limit: queryArg.limit }
        }),
        providesTags: ['Users']
      }),
      sendDownloadLink: build.mutation<
        SendDownloadLinkApiResponse,
        SendDownloadLinkApiArg
      >({
        query: queryArg => ({
          url: `/send-download-link`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Users']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type AddUserDeviceTokensApiResponse =
  /** status 201 UserDeviceTokens has been created successfully. */ {
    ok?: boolean;
    data?: UserDeviceTokens;
    message?: string;
  };
export type AddUserDeviceTokensApiArg = {
  /** UserDeviceTokens that needs to be added to the application */
  createUserDeviceTokens: CreateUserDeviceTokens;
};
export type GetUsersApiResponse = /** status 200 Users found. */ {
  ok?: boolean;
  data?: Users;
  message?: string;
};
export type GetUsersApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type AddUsersApiResponse =
  /** status 201 Users has been created successfully. */ {
    ok?: boolean;
    data?: Users;
    message?: string;
  };
export type AddUsersApiArg = {
  /** Users that needs to be added to the application */
  createUsers: CreateUsers;
};
export type GetUsersByIdApiResponse = /** status 200 Users details found. */ {
  ok?: boolean;
  data?: Users;
  message?: string;
};
export type GetUsersByIdApiArg = {
  /** ID of the Users to retrieve */
  id: string;
};
export type UpdateUsersApiResponse =
  /** status 200 The Users's details has been updated successfully */ {
    ok?: boolean;
    data?: Users;
    message?: string;
  };
export type UpdateUsersApiArg = {
  /** ID of the Users to update */
  id: string;
  /** Users that needs to be added to the application */
  updateUsers: UpdateUsers;
};
export type DeleteUsersApiResponse =
  /** status 200 The Users's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type DeleteUsersApiArg = {
  /** ID of the Users to delete */
  id: string;
  /** flag to hard delete */
  delete?: string;
};
export type BlockUnblockUserApiResponse =
  /** status 200 User block/unblock status updated successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type BlockUnblockUserApiArg = {
  /** ID of the user to block or unblock */
  id: string;
  /** Flag indicating the action to perform */
  blockUnblockUser: BlockUnblockUser;
};
export type BlockUnblockUser = {
  flag: {
    type?: string;
    enum?: string[];
    example?: string;
    description?: string;
  };
};
export type ForgotPasswordApiResponse =
  /** status 200 The Users's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type ForgotPasswordApiArg = {
  /** Email address of the user */
  body: {};
};
export type ResetPasswordSchema = {
  password: string;
  userId: string;
};
export type ResetPasswordApiResponse = {
  ok?: boolean;
  data?: string | null;
  message?: string;
};

export type VerifySignupCodeApiResponse =
  /** status 200 The signup code has been verified successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type ResetPasswordApiArg = {
  /** Users that needs to be added to the application */
  resetPasswordSchema: ResetPasswordSchema;
};
export type VerifySignupCodeApiArg = {
  /** Signup code to verify */
  body: {
    code?: string;
  };
};
export type PostForgotPasswordApiResponse =
  /** status 200 The Users's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type PostForgotPasswordApiArg = {
  /** Email address of the user */
  body: {};
};
export type IsEmailExistApiResponse =
  /** status 200 The email exists in the system. */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type IsEmailExistApiArg = {
  /** Email address to check if it exists */
  isEmailExist: IsEmailExist;
};
export type MeApiResponse =
  /** status 201 Users has been created successfully. */ {
    ok?: boolean;
    data?: User;
    message?: string;
  };
export type MeApiArg = void;
export type SearchUsersApiResponse =
  /** status 200 Users found successfully */ {
    ok?: boolean;
    data?: {
      userId?: string;
      fullName?: string;
      profileImage?: string | null;
    }[];
    message?: string;
  };
export type SearchUsersApiArg = {
  /** Search term for user full name (supports partial matching) */
  q: string;
  /** Maximum number of results to return (default: 10) */
  limit?: number;
};
export type SendDownloadLinkApiResponse =
  /** status 200 Download links sent successfully */ {
    ok?: boolean;
    data?: {
      success?: {
        userId?: string;
        email?: string;
        name?: string;
      }[];
      failed?: {
        userId?: string;
        reason?: string;
      }[];
    };
    message?: string;
  };
export type SendDownloadLinkApiArg = {
  /** User IDs and download link */
  body: {
    userIds?: string[];
    link?: string;
  };
};
export type UserDeviceTokens = {
  count?: number;
  rows?: {
    id: string;
    tokens: string;
    userId: string;
    status: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
  }[];
};
export type CreateUserDeviceTokens = {
  tokens: string;
};
export type Users = {
  count?: number;
  rows?: {
    email: string;
    password: string;
    fullName: string;
    planType: string;
    invited: string;
    subscriberId?: string;
    tokens: string;
  }[];
};
export type CreateUsers = {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  fullName?: string;
  planType?: string;
  invited?: string;
  cognitoUserName?: string;
};
export type UpdateUsers = {
  fullName?: string;
  isInvited?: string;
  isFirstLogin?: boolean;
  isVerified?: string;
  status?: string;
  cognitoUserName?: string;
};

export type IsEmailExist = {
  email?: string;
};
export type User = {
  email: string;
  fullName: string;
  isInvited: string;
  isFirstLogin: boolean;
  haveSubscription?: boolean;
  userId: string;
  subscriberId: string;
  permissions?: string[];
  isAdmin?: boolean;
  roleName?: string;
  userDetail?: {
    id?: string;
    profileImage?: any;
    firstName?: string;
    lastName?: string;
  };
  subscription?: {
    accessAllowed?: boolean;
  };
};
export const {
  useAddUserDeviceTokensMutation,
  useGetUsersQuery,
  useAddUsersMutation,
  useGetUsersByIdQuery,
  useUpdateUsersMutation,
  useDeleteUsersMutation,
  useBlockUnblockUserMutation,
  useForgotPasswordMutation,
  useVerifySignupCodeMutation,
  useResetPasswordMutation,
  usePostForgotPasswordMutation,
  useIsEmailExistMutation,
  useMeQuery,
  useSearchUsersQuery,
  useSendDownloadLinkMutation
} = injectedRtkApi;
